import React, { useEffect } from "react";
import { BrowserRouter, Route, Switch, Redirect, useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import queryString from "query-string";
import PropTypes from "prop-types";
import omit from "lodash/fp/omit";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/pro-solid-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { far } from "@fortawesome/pro-regular-svg-icons";
import { fal } from "@fortawesome/pro-light-svg-icons";
import { fad } from "@fortawesome/pro-duotone-svg-icons";
import { ThemeProvider } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import { Alert } from "@material-ui/lab";
import { SnackbarProvider, useSnackbar } from "notistack";

import "./App.css";
import CookieConsent from "components/CookieConsent";
import AuthPage from "components/AuthPage";
import AuthPopupPage from "components/AuthPopupPage";
import PlanStay from "components/PlanStay";
import FacilityPage from "components/FacilityPage";
import EmbeddedFacilityBookingPage from "components/EmbeddedFacilityBookingPage";
import EmbeddedAllFacilitiesBookingPage from "components/EmbeddedAllFacilitiesBookingPage";
import ChooseRoom from "components/ChooseRoom";
import Checkout from "components/Checkout";
import ConfirmStay from "components/ConfirmStay";
import FinalizeBooking from "components/FinalizeBooking";
import Error from "components/Error";
import ErrorBoundary from "components/ErrorBoundary";
import FindBookingPage from "components/FindBookingPage";
import ManageBooking from "components/ManageBooking";
import Account from "components/Account";
import SelectAddOns from "components/SelectAddOns";
import { fetchProperties } from "features/property/propertySlice";
import { setMobileAppUser } from "features/user/userSlice";
import { useThemeManager } from "hooks/useThemeManager";
import { useSourceManager } from "hooks/useSourceManager";
import Routes from "config/routes";

import { giveCookieConsent, setAnalyticsSource, trackError, trackPageView } from "utils/analytics";
import { PROFILE_SECTIONS } from "utils/constants";
import { initFirebase } from "utils/firebase/firebase";
import { useSignInWithFirebaseToken, useSignOutUser } from "hooks";
import { isTokenExpired } from "utils/jwTokenUtils";

library.add(fas, far, fab, fal, fad);

const AppContent = ({ history }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const signOutUser = useSignOutUser();

  // Use the custom source manager hook to initialize and manage booking source
  useSourceManager(history);

  /* BEGIN NOTISTACK CUSTOMIZATION */
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const closeOnClick = () => closeSnackbar();
  const handleNotifOpen = (message, options = {}) => {
    options = {
      TransitionProps: { direction: "up" },
      anchorOrigin: { horizontal: "center", vertical: "bottom" },
      autoHideDuration: 4000,
      onClose: closeOnClick,
      content: (
        <Alert onClose={closeOnClick} severity={options.variant}>
          {message}
        </Alert>
      ),
      ...options,
    };
    enqueueSnackbar(message, options);

    if (options.variant === "error") {
      trackError(message, true);
    }
  };
  /* END NOTISTACK CUSTOMIZATION */

  useEffect(() => {
    dispatch(fetchProperties());
  }, []);

  useEffect(() => {
    const searchParams = queryString.parse(history.location.search);

    if (searchParams.source) {
      setAnalyticsSource(searchParams.source);
    }

    // Track app users
    dispatch(setMobileAppUser(searchParams.source === "mobileapp"));

    // Track the first page view, then listen for history changes
    trackPageView(history.location.pathname);
    history.listen((location) => {
      trackPageView(location.pathname);
    });
  }, [history]);

  const authCompleted = () => {
    // Redirect user to the page they were trying to access before being redirected to login
    const redirectPath = history.location.state?.redirectPath;

    history.push(redirectPath || Routes.PLAN_STAY);
  };
  const redirectAuth = (redirectPath) => {
    history.push({
      pathname: Routes.LOGIN,
      search: history.location.search,
      state: {
        redirectPath,
      },
    });
  };

  let promoCode = "";
  let corporateCode = "";

  const handlePromoAndCorporateCodes = (props) => {
    //Will fetch and remove promo code and corporate code if found in query params and up
    const searchParams = queryString.parse(props.location.search);

    promoCode = searchParams.promoCode || props.location.state?.promoCode;
    corporateCode = searchParams.corporateCode || props.location.state?.corporateCode;

    if (searchParams.promoCode || searchParams.corporateCode) {
      props.history.replace({
        search: queryString.stringify(omit(["promoCode", "corporateCode"])(searchParams)),
        state: {
          promoCode,
          corporateCode,
        },
      });

      return <div></div>;
    }
  };

  // Setup firebase (auth and analytics)
  initFirebase();

  // Re-authenticate the user if needed. If there's an auth token
  // in the redux store, grab it and try to reauthenticate with it
  const authToken = useSelector((state) => state.user.firebaseToken);

  // Initial sign-in verification (only on app load)
  useSignInWithFirebaseToken(authToken);

  // Check token expiry on route changes
  useEffect(() => {
    const handleExpiredToken = async () => {
      await signOutUser();
      handleNotifOpen("Your session has expired. Please sign in again.", {
        variant: "error",
      });
    };

    if (authToken && isTokenExpired(authToken)) {
      handleExpiredToken();
    }
  }, [location.pathname, authToken]);

  return (
    <ErrorBoundary
      fallbackUI={(_hasReactError) => <PlanStay history={history} onNotifOpen={handleNotifOpen} />}>
      <Switch>
        <Route exact path="/">
          <Redirect to={Routes.PLAN_STAY} />
        </Route>
        <Route
          path={Routes.PLAN_STAY}
          render={(props) => <PlanStay {...props} onNotifOpen={handleNotifOpen} />}
        />
        <Route
          path={`${Routes.PROPERTIES}/:propertySlug${Routes.EMBEDDED_FACILITY_BOOK}/:facilitySlug/:facilityServiceId`}
          render={(props) => (
            <EmbeddedFacilityBookingPage {...props} onNotifOpen={handleNotifOpen} />
          )}
        />
        <Route
          path={`${Routes.PROPERTIES}/:propertySlug${Routes.EMBEDDED_FACILITY_BOOK}`}
          render={(props) => (
            <EmbeddedAllFacilitiesBookingPage {...props} onNotifOpen={handleNotifOpen} />
          )}
        />
        <Route
          path={`${Routes.PROPERTIES}/:propertySlug${Routes.EMBEDDED_FACILITY_ENQUIRY}/:facilitySlug/:facilityServiceId`}
          render={(props) => (
            <EmbeddedFacilityBookingPage
              launchInEnquiryMode={true}
              {...props}
              onNotifOpen={handleNotifOpen}
            />
          )}
        />
        <Route
          path={`${Routes.PROPERTIES}/:propertySlug${Routes.EMBEDDED_FACILITY_ENQUIRY}`}
          render={(props) => (
            <EmbeddedAllFacilitiesBookingPage
              launchInEnquiryMode={true}
              {...props}
              onNotifOpen={handleNotifOpen}
            />
          )}
        />
        <Route
          path={`${Routes.PROPERTIES}/:propertySlug${Routes.FACILITIES}/:facilitySlug`}
          render={(props) => <FacilityPage {...props} onNotifOpen={handleNotifOpen} />}
        />
        <Route
          path={Routes.CHOOSE_ROOM}
          render={(props) => {
            handlePromoAndCorporateCodes(props);

            return (
              <ChooseRoom
                {...props}
                onNotifOpen={handleNotifOpen}
                promoCode={promoCode}
                corporateCode={corporateCode}
              />
            );
          }}
        />
        <Route
          path={Routes.SELECT_ADDONS}
          render={(props) => <SelectAddOns {...props} onNotifOpen={handleNotifOpen} />}
        />
        <Route
          path={Routes.CHECKOUT}
          render={(props) => <Checkout {...props} onNotifOpen={handleNotifOpen} />}
        />
        <Route
          path={Routes.CONFIRM_STAY}
          render={(props) => <ConfirmStay {...props} onNotifOpen={handleNotifOpen} />}
        />
        <Route
          path={Routes.FINALIZE_BOOKING}
          render={(props) => <FinalizeBooking {...props} onNotifOpen={handleNotifOpen} />}
        />
        <Route
          path={Routes.ACCOUNT_BOOKINGS}
          render={(props) => (
            <Account
              {...props}
              onNotifOpen={handleNotifOpen}
              redirectAuth={redirectAuth}
              showDetails={PROFILE_SECTIONS.BOOKINGS}
            />
          )}
        />
        <Route
          path={Routes.ACCOUNT}
          render={(props) => (
            <Account
              {...props}
              onNotifOpen={handleNotifOpen}
              redirectAuth={redirectAuth}
              showDetails={PROFILE_SECTIONS.DETAILS}
            />
          )}
        />
        <Route
          path={Routes.LOGIN}
          render={(props) => (
            <AuthPage
              {...props}
              login
              onNotifOpen={handleNotifOpen}
              authCompleted={authCompleted}
            />
          )}
        />
        <Route
          path={Routes.SIGNUP}
          render={(props) => (
            <AuthPage
              {...props}
              signUp
              onNotifOpen={handleNotifOpen}
              authCompleted={authCompleted}
            />
          )}
        />
        <Route
          path={Routes.AUTH_POPUP}
          render={(props) => <AuthPopupPage {...props} onNotifOpen={handleNotifOpen} />}
        />
        <Route
          path={Routes.FIND_BOOKING}
          render={(props) => <FindBookingPage {...props} onNotifOpen={handleNotifOpen} />}
        />
        <Route
          path={Routes.MANAGE_BOOKING}
          render={(props) => <ManageBooking {...props} onNotifOpen={handleNotifOpen} />}
        />
        <Route component={Error} />
      </Switch>
      <CookieConsent onAgree={giveCookieConsent} />
    </ErrorBoundary>
  );
};

const App = () => {
  const history = useHistory();

  // Use the custom theme manager hook to initialize and manage the skin theme applied to the app
  const { currentTheme } = useThemeManager(history);
  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={1}>
        <AppContent history={history} />
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default function WithBrowserRouter() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}

AppContent.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
    goBack: PropTypes.func,
    listen: PropTypes.func,
    location: PropTypes.object,
  }),
  location: PropTypes.object,
};
